import React from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { INR, SPECIALIZATION_LABEL, SPECIALIZATION_COLOR } from "@/lib/constants";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const VOLT = "#CCFF00";

export default function ReportsPage() {
  const { activeBranchId } = useAuth();
  const params = activeBranchId ? { branch_id: activeBranchId } : {};
  const { data: capacity } = useQuery({
    queryKey: ["reports-overview", activeBranchId],
    queryFn: async () => (await apiClient.get("/dashboard/overview", { params })).data,
  });
  const { data: coaches = [] } = useQuery({
    queryKey: ["reports-coaches", activeBranchId],
    queryFn: async () => (await apiClient.get("/dashboard/coach-performance", { params })).data,
  });
  const { data: branches = [] } = useQuery({
    queryKey: ["branches"], queryFn: async () => (await apiClient.get("/branches")).data,
  });

  const cap = capacity?.capacity || [];

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="reports-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Owner Reports</div>
        <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Capacity & KPI Reports</h1>
        <div className="text-zinc-500 text-sm mt-1">Capacity utilization · revenue · coach standings — board-ready.</div>
      </div>

      <Panel>
        <PanelHeader title="Branch Capacity Utilization" subtitle="Active members / total capacity" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {cap.map((c) => {
            const pct = c.utilization_pct || 0;
            const color = pct >= 70 ? "#00E676" : pct >= 40 ? "#CCFF00" : "#FF3B30";
            return (
              <div key={c.branch_id} className="bg-zinc-950 border border-zinc-900 rounded-sm p-5" data-testid={`report-branch-${c.branch_id}`}>
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">{c.branch_name}</div>
                <div className="flex items-baseline justify-between mt-2">
                  <div className="font-display text-6xl font-black" style={{ color }}>
                    {pct}<span className="text-2xl text-zinc-500">%</span>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl font-bold text-white">{c.active}</div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">of {c.capacity}</div>
                  </div>
                </div>
                <div className="h-2 mt-4 bg-zinc-900 rounded-sm overflow-hidden">
                  <div className="h-full" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      </Panel>

      <Panel>
        <PanelHeader title="Coach Conversion vs Score" subtitle="Sales effectiveness" />
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={coaches} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
            <CartesianGrid stroke="#1f1f23" vertical={false} />
            <XAxis dataKey="name" stroke="#52525B" fontSize={10} angle={-20} textAnchor="end" height={60} />
            <YAxis stroke="#52525B" fontSize={10} />
            <Tooltip contentStyle={{ background: "#0c0c0c", border: "1px solid #27272a", borderRadius: 4 }} />
            <Bar dataKey="conversion_pct" name="Conversion %" fill={VOLT} radius={[3, 3, 0, 0]} maxBarSize={28} />
            <Bar dataKey="performance_score" name="Score" fill="#7DD3FC" radius={[3, 3, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>

      <Panel>
        <PanelHeader title="Revenue Summary" subtitle="Aggregated" />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-4 bg-zinc-950 border border-zinc-900 rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Memberships</div>
            <div className="font-display text-4xl font-black text-volt mt-2">{INR(capacity?.revenue_memberships)}</div>
          </div>
          <div className="p-4 bg-zinc-950 border border-zinc-900 rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Won Leads</div>
            <div className="font-display text-4xl font-black text-volt mt-2">{INR(capacity?.revenue_won)}</div>
          </div>
          <div className="p-4 bg-zinc-950 border border-zinc-900 rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Conversion</div>
            <div className="font-display text-4xl font-black text-volt mt-2">{capacity?.conversion_pct || 0}%</div>
          </div>
        </div>
      </Panel>

      <RenewalTrendPanel branchId={activeBranchId} />
    </div>
  );
}

function RenewalTrendPanel({ branchId }) {
  const params = { windows: "30,60,90" };
  if (branchId) params.branch_id = branchId;
  const { data } = useQuery({
    queryKey: ["renewal-trend", branchId],
    queryFn: async () => (await apiClient.get("/reports/renewal-trend", { params })).data,
  });
  const rows = data?.windows || [];
  const rateColor = (pct) => pct >= 70 ? "#00E676" : pct >= 50 ? VOLT : pct >= 30 ? "#FBBF24" : "#FF3B30";

  return (
    <Panel>
      <PanelHeader
        title="Renewal Rate · 30 / 60 / 90 day"
        subtitle="% of expired memberships that renewed within 30 days of expiry"
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="renewal-trend-tiles">
        {rows.map((r) => {
          const pct = r.renewal_rate_pct || 0;
          const color = rateColor(pct);
          return (
            <div key={r.window_days} className="bg-zinc-950 border border-zinc-900 rounded-sm p-5" data-testid={`renewal-window-${r.window_days}`}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Last {r.window_days} days</div>
              <div className="font-display text-5xl font-black mt-2" style={{ color }}>{pct}%</div>
              <div className="text-[11px] text-zinc-500 mt-1">
                {r.renewed} of {r.expired} expired members renewed
              </div>
              <div className="h-1.5 mt-3 bg-zinc-900 rounded-sm overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
              </div>
            </div>
          );
        })}
      </div>
      {rows.length > 0 && (
        <div className="mt-4">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
              <CartesianGrid stroke="#1f1f23" vertical={false} />
              <XAxis dataKey="window_days" tickFormatter={(v) => `${v}d`} stroke="#52525B" fontSize={10} />
              <YAxis stroke="#52525B" fontSize={10} unit="%" />
              <Tooltip formatter={(v) => `${v}%`} contentStyle={{ background: "#0c0c0c", border: "1px solid #27272a", borderRadius: 4 }} />
              <Bar dataKey="renewal_rate_pct" name="Renewal %" fill={VOLT} radius={[3, 3, 0, 0]} maxBarSize={64} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}
